import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { stringify } from '../../../../util/urlHelpers';
import { isFilterEnabled } from '../../../../util/search';
import { makeGetListingsByIdSelector } from '../../../../ducks/marketplaceData.duck';
import { fetchFeaturedRentals } from '../../../../ducks/featuredRentals.duck';

import { ErrorMessage, IconSpinner, ListingCard, NamedLink } from '../../../../components';

import { hasDataInFields } from '../../Field';
import SectionContainer from '../../SectionBuilder/SectionContainer';
import SectionTitle from '../SectionTitle';
import FilterComponent from '../../../SearchPage/FilterComponent';
import {
  MotionSection,
  fadeUp,
  staggerContainer,
  staggerItem,
  viewportOnce,
} from '../scrollAnimations';

import filterIcon from '../../../../assets/search/icon-filter.svg';

import css from './FeaturedRentals.module.css';

const FILTER_DROPDOWN_OFFSET = -14;
const VISIBLE_CARD_COUNT = 4;
const CARD_SIZES = '(max-width: 549px) 100vw, (max-width: 1023px) 50vw, 25vw';

// Figma (290:890): Occasion, Designer, Size, Colour — same fields and order as
// the search-page pills. Options come from hosted config.
const FILTER_KEYS = ['occasion', 'designer', 'size_clothing', 'color'];

/**
 * Listing fields that the Featured Rentals row filters on, in Figma order.
 *
 * @param {Array<Object>} listingFields hosted listing field configs
 * @returns {Array<Object>}
 */
const getFeaturedRentalFilters = listingFields => {
  const byKey = new Map((listingFields || []).map(field => [field.key, field]));
  return FILTER_KEYS.map(key => byKey.get(key)).filter(
    field => field && isFilterEnabled(field.filterConfig || {}) && field.enumOptions?.length > 0
  );
};

/**
 * Homepage Featured Rentals: curated listings (`meta_isfeatured=yes`) under the
 * Console title, with SearchPage filter pills and a four-card row.
 *
 * Rendered when Page Builder sectionId is `featured-rentals`. Hidden entirely
 * when there are no featured listings (and no active filters).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {Object?} props.title
 * @param {Object?} props.appearance
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const FeaturedRentals = props => {
  const { sectionId, className, rootClassName, title, appearance, options } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const dispatch = useDispatch();

  const [currentQueryParams, setCurrentQueryParams] = useState({});

  const getListingsById = useMemo(makeGetListingsByIdSelector, []);
  const { listingIds = [], totalItems = 0, inProgress, fetched, error } = useSelector(
    state => state.featuredRentals || {}
  );
  const listings = useSelector(state => getListingsById(state, listingIds));

  const listingImageConfig = config.layout.listingImage;
  const listingFields = config?.listing?.listingFields || [];
  const listingCategories = config.categoryConfiguration?.categories || [];
  const marketplaceCurrency = config.currency;
  const availableFilters = useMemo(() => getFeaturedRentalFilters(listingFields), [listingFields]);

  const filterQueryKey = stringify(currentQueryParams);

  useEffect(() => {
    dispatch(
      fetchFeaturedRentals({
        queryParams: currentQueryParams,
        listingImageConfig,
      })
    );
    // listingImageConfig is stable for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterQueryKey]);

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title], fieldOptions);

  const visibleListings = listings.slice(0, VISIBLE_CARD_COUNT);
  const showViewAll = (totalItems || 0) > VISIBLE_CARD_COUNT;
  const hasActiveFilters = Object.keys(currentQueryParams).length > 0;
  const viewAllSearch = stringify({
    meta_isfeatured: 'yes',
    pub_listingType: 'rent',
    ...currentQueryParams,
  });

  const initialValues = queryParamNames => {
    const names = Array.isArray(queryParamNames) ? queryParamNames : [];
    return names.reduce((acc, name) => {
      if (currentQueryParams[name]) {
        acc[name] = currentQueryParams[name];
      }
      return acc;
    }, {});
  };

  const getHandleChangedValueFn = () => updatedURLParams => {
    setCurrentQueryParams(prev => {
      const next = { ...prev };
      Object.entries(updatedURLParams || {}).forEach(([key, value]) => {
        if (value == null || value === '') {
          delete next[key];
        } else {
          next[key] = value;
        }
      });
      return next;
    });
  };

  // Stay off the page until featured listings exist. Filter-empty still shows
  // the toolbar so the visitor can clear pills; unfiltered empty hides the
  // whole `featured-rentals` section.
  if (!hasActiveFilters && visibleListings.length === 0) {
    return null;
  }

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(css.root, className)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <MotionSection>
        <div className={css.inner}>
          {hasHeaderFields ? (
            <motion.header
              className={css.header}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              <SectionTitle data={title} className={css.title} />
            </motion.header>
          ) : null}

          <motion.div
            className={css.filterHeader}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            <div className={css.filterHeaderSpacer} />
            <div className={css.filterCluster}>
              <div className={css.toolbarLabel}>
                <img className={css.toolbarIcon} src={filterIcon} alt="" width={20} height={20} />
                <FormattedMessage id="SearchPage.filterLabel" />
              </div>
              <span className={css.toolbarDivider} aria-hidden="true" />
              <div className={css.filterPills}>
                {availableFilters.map(filterConfig => {
                  const key = `FeaturedRentals.${filterConfig.scope || 'public'}.${
                    filterConfig.key
                  }`;
                  const filterId = `FeaturedRentals.${filterConfig.key.toLowerCase()}`;
                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      className={css.filter}
                      config={filterConfig}
                      containerId="FeaturedRentals_filters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={currentQueryParams}
                      initialValues={initialValues}
                      getHandleChangedValueFn={getHandleChangedValueFn}
                      intl={intl}
                      showAsPopup
                      openerVariant="pill"
                      contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                    />
                  );
                })}
              </div>
            </div>
            {showViewAll ? (
              <NamedLink
                name="SearchPage"
                to={{ search: `?${viewAllSearch}` }}
                className={css.viewAll}
              >
                <FormattedMessage id="SectionListings.viewAll" />
              </NamedLink>
            ) : (
              <div className={css.filterHeaderSpacer} />
            )}
          </motion.div>

          <div className={css.results}>
            {(!fetched || (inProgress && visibleListings.length === 0)) && !error ? (
              <IconSpinner className={css.spinner} />
            ) : null}
            {error ? (
              <div className={css.message} role="alert">
                <h4 className={css.messageTitle}>
                  <FormattedMessage id="SectionListings.genericErrorTitle" />
                </h4>
                <ErrorMessage error={error} />
              </div>
            ) : null}
            {!error && fetched && visibleListings.length === 0 && !inProgress ? (
              <div className={css.message} role="status">
                <p>
                  <FormattedMessage id="SectionListings.noListingsFoundInfo" />
                </p>
              </div>
            ) : null}
            {visibleListings.length > 0 ? (
              <motion.ul
                className={classNames(css.grid, { [css.gridLoading]: inProgress })}
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOnce}
              >
                {visibleListings.map(listing => (
                  <motion.li key={listing.id.uuid} className={css.cardItem} variants={staggerItem}>
                    <ListingCard
                      listing={listing}
                      layout="editorial"
                      showAuthorInfo={false}
                      renderSizes={CARD_SIZES}
                      lazyLoadImage
                    />
                  </motion.li>
                ))}
              </motion.ul>
            ) : null}
          </div>
        </div>
      </MotionSection>
    </SectionContainer>
  );
};

export default FeaturedRentals;
